/**
 * BetaOfferBanner — iter161
 *
 * Renders the "Exclusive Beta Launch Offer" for eligible individual
 * accounts. The backend `/api/beta-offer/eligibility` endpoint is the
 * single source of truth — this component just paints one of three
 * states (or nothing at all).
 *
 *   state === "verification_required"  → reminder to complete KYC
 *   state === "eligible"                → 🎁 activate button
 *   state === "activated"               → ✅ confirmation + expiry
 *   anything else                       → renders nothing
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB",
      { year: "numeric", month: "long", day: "numeric" });
  } catch (_e) { return ""; }
}

// Shared shell — keeps padding, radius, and dark palette consistent with
// the rest of the dashboard cards. Extracted above the parent to satisfy
// the "no unstable nested components" lint rule.
function Shell({ icon, title, body, cta, testId, err, lang }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 sm:p-6 mb-6"
      style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a140a 60%,#3d2f0e 100%)", color: "#f5efe1" }}
      data-testid={testId}
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-20"
           style={{ background: "radial-gradient(circle,#e8c873 0%,transparent 70%)" }} />
      <div className="relative flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
             style={{ background: "rgba(232,200,115,0.15)", border: "1px solid rgba(232,200,115,0.4)" }}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: "#e8c873" }}>
            {lang === "ar" ? "عرض الإطلاق الحصري" : "Exclusive Launch Offer"}
          </div>
          <div className="mt-1 text-lg sm:text-xl font-bold">{title}</div>
          <div className="mt-1 text-sm text-[#e5ddc8] leading-relaxed">{body}</div>
        </div>
        {cta && <div className="w-full sm:w-auto shrink-0 sm:self-center">{cta}</div>}
      </div>
      {err && <div className="relative mt-3 text-sm text-red-300" data-testid="beta-offer-error">{err}</div>}
    </div>
  );
}

export default function BetaOfferBanner() {
  const { lang } = useI18n();
  const [state, setState] = useState(null);
  const [err, setErr] = useState("");
  const [activating, setActivating] = useState(false);

  const load = () => {
    apiClient.get("/beta-offer/eligibility")
      .then(({ data }) => setState(data))
      .catch(() => setState({ state: "hidden" }));
  };
  useEffect(load, []);

  if (!state || state.state === "hidden") return null;

  const activate = async () => {
    setActivating(true);
    setErr("");
    try {
      const { data } = await apiClient.post("/beta-offer/activate");
      setState(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((typeof d === "string" ? d : d?.[lang] || d?.ar) || "خطأ");
    } finally {
      setActivating(false);
    }
  };

  if (state.state === "verification_required") {
    return (
      <Shell
        lang={lang}
        err={err}
        testId="beta-offer-reminder"
        icon={<ShieldCheck className="w-6 h-6" style={{ color: "#e8c873" }} />}
        title={lang === "ar"
          ? "أكمل توثيق هويتك واحصل على باقة كارتال بلس للأفراد مجانًا لمدة 6\u00A0أشهر"
          : "Verify your identity and unlock 6 months of Individual Plus — free"}
        body={lang === "ar"
          ? "بعد اعتماد الإدارة لتوثيقك، سيظهر لك زر التفعيل تلقائيًا في هذه الصفحة."
          : "Once admin approves your verification, the activation button will appear here automatically."}
        cta={
          <Link
            to={state.verification_url || "/verify"}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap"
            style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
            data-testid="beta-offer-verify-cta"
          >
            {lang === "ar" ? "ابدأ التوثيق" : "Start verification"}
          </Link>
        }
      />
    );
  }

  if (state.state === "eligible") {
    return (
      <Shell
        lang={lang}
        err={err}
        testId="beta-offer-eligible"
        icon={<Gift className="w-6 h-6" style={{ color: "#e8c873" }} />}
        title={lang === "ar"
          ? "أنت مؤهل! فعّل 6\u00A0أشهر من باقة كارتال بلس للأفراد مجانًا"
          : "You're eligible — activate 6 months of Individual Plus for free"}
        body={lang === "ar"
          ? "180 يومًا كاملة بمزايا Plus بدون أي رسوم. التفعيل مرة واحدة فقط."
          : "180 full days of Plus benefits at 0 SAR. One-time activation."}
        cta={
          <button
            onClick={activate}
            disabled={activating}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
            data-testid="beta-offer-activate-btn"
          >
            {activating
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Gift className="w-4 h-4" />}
            {lang === "ar" ? "🎁 تفعيل العرض المجاني" : "🎁 Activate free offer"}
          </button>
        }
      />
    );
  }

  if (state.state === "activated") {
    return (
      <Shell
        lang={lang}
        err={err}
        testId="beta-offer-activated"
        icon={<Sparkles className="w-6 h-6" style={{ color: "#e8c873" }} />}
        title={lang === "ar" ? "✅ تم تفعيل العرض" : "✅ Offer activated"}
        body={lang === "ar"
          ? `اشتراك باقة كارتال بلس للأفراد نشط حتى ${fmtDate(state.expires_at, lang)}.`
          : `Your Individual Plus is active until ${fmtDate(state.expires_at, lang)}.`}
        cta={null}
      />
    );
  }

  return null;
}
